import React from 'react';
import { Minus, Plus, X, Truck, CreditCard, ChevronLeft, Tag } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../context/CartContext';
import { applyPromocodeToCart } from '../services/promocodes';
import UpsellSlider from '../components/UpsellSlider';

export default function Cart() {
  const navigate = useNavigate();
  const { 
    items, 
    updateQuantity, 
    removeItem, 
    appliedPromocode, 
    setAppliedPromocode,
    promocodeValidationMessage,
    setPromocodeValidationMessage
  } = useCart();
  const [promocodeInput, setPromocodeInput] = React.useState('');
  const [promocodeError, setPromocodeError] = React.useState('');
  const [isApplyingPromocode, setIsApplyingPromocode] = React.useState(false);

  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const shipping = 0; // Free shipping
  const promocodeDiscount = appliedPromocode ? appliedPromocode.discountAmount : 0;
  const total = subtotal + shipping - promocodeDiscount;

  const applyPromocode = async () => {
    if (!promocodeInput.trim()) {
      setPromocodeError('Palun sisesta sooduskood');
      return;
    }

    setIsApplyingPromocode(true);
    setPromocodeError('');
    setPromocodeValidationMessage(''); // Clear any previous validation messages

    try {
      const appliedCode = await applyPromocodeToCart(promocodeInput, subtotal);
      setAppliedPromocode(appliedCode);
      setPromocodeInput('');
    } catch (error) {
      setPromocodeError(error instanceof Error ? error.message : 'Viga sooduskoodi rakendamisel');
    } finally {
      setIsApplyingPromocode(false);
    }
  };

  const removePromocode = () => {
    setAppliedPromocode(null);
    setPromocodeInput('');
    setPromocodeError('');
    setPromocodeValidationMessage('');
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div className="container mx-auto py-12 px-4 md:px-6">
      <div className="flex items-center gap-4 mb-8">
        <Link
          to="/toukeratas"
          className="flex items-center text-gray-600 hover:text-[#4FC1E9]"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Tagasi toodetele
        </Link>
        <h1 className="text-3xl font-bold">Ostukorv</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-8">
          {items.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <p className="text-gray-600 mb-4">Sinu ostukorv on tühi</p>
              <Link
                to="/toukeratas"
                className="text-[#4FC1E9] hover:text-[#3ba8d0] font-medium"
              >
                Vaata tooteid
              </Link>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              {items.map((item) => (
                <div key={item.id} className="p-6 border-b last:border-b-0">
                  <div className="flex items-center">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                    <div className="ml-6 flex-1">
                      <div className="flex justify-between items-start">
                        <h3 className="text-lg font-semibold">{item.name}</h3>
                        <button 
                          onClick={() => removeItem(item.id)}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="mt-4 flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="p-1 rounded-full hover:bg-gray-100"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-8 text-center">{item.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="p-1 rounded-full hover:bg-gray-100"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <span className="font-bold text-[#4FC1E9]">{item.price.toFixed(2)}€</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-4">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-6">Tellimuse kokkuvõte</h2>
            
            <div className="space-y-4">
              <div className="flex justify-between">
                <span>Vahesumma:</span>
                <span>{subtotal.toFixed(2)}€</span>
              </div>
              {appliedPromocode && (
                <div className="flex justify-between text-green-600">
                  <div className="flex items-center">
                    <Tag className="w-4 h-4 mr-1" />
                    <span>Sooduskood ({appliedPromocode.code}):</span>
                    <button 
                      onClick={removePromocode}
                      className="ml-2 text-red-500 hover:text-red-700"
                      title="Eemalda sooduskood"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <span>-{promocodeDiscount.toFixed(2)}€</span>
                </div>
              )}
              
              {!appliedPromocode && (
                <div className="border-t pt-4">
                  {/* Show validation message if promocode became invalid */}
                  {promocodeValidationMessage && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-amber-800 text-sm">
                        <strong>Sooduskood eemaldatud:</strong> {promocodeValidationMessage}
                      </p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Sooduskood</label>
                    <div className="flex gap-2 w-full min-w-0">
                      <input
                        type="text"
                        value={promocodeInput}
                        onChange={(e) => setPromocodeInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && applyPromocode()}
                        placeholder="Sisesta sooduskood"
                        className="flex-1 min-w-0 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#4FC1E9] focus:border-transparent text-sm"
                        disabled={isApplyingPromocode}
                      />
                      <button
                        onClick={applyPromocode}
                        disabled={isApplyingPromocode || !promocodeInput.trim()}
                        className="flex-shrink-0 px-4 py-2 bg-[#4FC1E9] text-white rounded-lg hover:bg-[#3ba8d0] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        {isApplyingPromocode ? 'Rakendab...' : 'Rakenda'}
                      </button>
                    </div>
                    {promocodeError && (
                      <p className="text-red-500 text-sm">{promocodeError}</p>
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex justify-between text-green-600">
                <span>Transport:</span>
                <span className="font-medium">TASUTA</span>
              </div>
              <div className="border-t pt-4">
                <div className="flex justify-between font-bold text-lg">
                  <span>Kokku:</span>
                  <span>{total.toFixed(2)}€</span>
                </div>
              </div>
            </div>

            <button 
              onClick={() => navigate('/kassa')}
              className="w-full bg-[#4FC1E9] hover:bg-[#3ba8d0] text-white font-bold py-3 px-6 rounded-full mt-6 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={items.length === 0}
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Valmis maksma?
            </button>

            <Link
              to="/toukeratas"
              className="block text-center mt-4 text-[#4FC1E9] hover:text-[#3ba8d0] font-medium"
            >
              Jätka ostlemist
            </Link>
          </div>
        </div>
      </div>

      {/* Upsell — parts & accessories */}
      <UpsellSlider />
    </div>
    </>
  );
}
